import { useState, useRef, useEffect, useCallback } from 'react';
import { QueryKeys } from '../../lib/QueryKeys';
import { useQueries } from '@tanstack/react-query';
import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { useParams } from '@tanstack/react-router';
import { Stop, Day, Itinerary, StopStateEnum } from '@city-sage/core-client';
import { useTranslation } from 'react-i18next';
import { ItineraryMapSection } from '../public-itinerary/ItineraryMapSection';
import { ItineraryOverviewSection } from '../public-itinerary/ItineraryOverviewSection';
import { ItineraryDaySection } from '../public-itinerary/ItineraryDaySection';
import { ItineraryStopSection } from '../public-itinerary/ItineraryStopSection';
import { ItineraryDaysOverviewSection } from '../public-itinerary/ItineraryDaysOverviewSection';
import { ItineraryAuthorSection } from '../public-itinerary/ItineraryAuthorSection';
import { ItineraryAuthorBanner } from '../public-itinerary/ItineraryAuthorBanner';
import { useMultipleDayStops } from '../../hooks/useMultipleDayStops';

export default function ItineraryPage() {
  const { id } = useParams({ from: '/itineraries/$id' });
  const { api } = useRepository();
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const [visibleStopId, setVisibleStopId] = useState<string | null>(null);
  const [visibleDayId, setVisibleDayId] = useState<string | null>(null);
  const [isOverviewVisible, setIsOverviewVisible] = useState<boolean>(false);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const overviewElementRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const stopElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const dayElementsRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isScrollingRef = useRef<boolean>(false);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const [
    {
      data: itinerary,
      isLoading: isLoadingItineraries,
      error: errorItineraries,
    },
    { data: days, isLoading: isLoadingDays, error: errorDays },
  ] = useQueries({
    queries: [
      {
        queryKey: QueryKeys.itinerary.details(id),
        queryFn: () => api.findOneItinerary({ id: id }),
        enabled: !!id,
      },
      {
        queryKey: QueryKeys.itinerary.days(id),
        queryFn: () =>
          api.findAllItineraryDay({
            // TODO this is hardcoded for now, we need to fix this
            limit: 1000,
            itineraryId: id,
            sort: JSON.stringify(['number', 'ASC']),
          }),
        enabled: !!id,
      },
    ],
  });

  // Fetch all stops for all days
  const { data: stopsData } = useMultipleDayStops({
    itineraryId: id || '',
    days,
    states: [StopStateEnum.Accepted],
  });

  // Set up intersection observer for stop visibility tracking
  useEffect(() => {
    const options = {
      root: null, // Use viewport as root for document scrolling
      rootMargin: '-20% 0px -20% 0px', // Only trigger when stop is well within view
      threshold: 0.3, // Adjusted for smoother free scrolling
    };

    observerRef.current = new IntersectionObserver((entries) => {
      // Skip intersection observer updates during programmatic scrolling
      if (isScrollingRef.current) {
        return;
      }

      let maxIntersectionRatio = 0;
      let newVisibleStopId: string | null = null;
      let newVisibleDayId: string | null = null;
      let newIsOverviewVisible = false;

      entries.forEach((entry) => {
        const stopId = (entry.target as HTMLElement).getAttribute(
          'data-stop-id'
        );
        const dayId = (entry.target as HTMLElement).getAttribute('data-day-id');
        const isOverview =
          (entry.target as HTMLElement).getAttribute('data-overview') ===
          'true';

        if (
          entry.isIntersecting &&
          entry.intersectionRatio > maxIntersectionRatio
        ) {
          maxIntersectionRatio = entry.intersectionRatio;

          if (isOverview) {
            newIsOverviewVisible = true;
            newVisibleStopId = null;
            newVisibleDayId = null;
          } else if (stopId) {
            newVisibleStopId = stopId;
            newVisibleDayId = null; // Clear day when stop is visible
            newIsOverviewVisible = false;
          } else if (dayId) {
            newVisibleDayId = dayId;
            newVisibleStopId = null; // Clear stop when day is visible
            newIsOverviewVisible = false;
          }
        }
      });

      // Batch state updates to avoid conflicts
      if (newIsOverviewVisible) {
        setIsOverviewVisible(true);
        setVisibleStopId(null);
        setVisibleDayId(null);
      } else if (newVisibleStopId) {
        setVisibleStopId(newVisibleStopId);
        setVisibleDayId(null);
        setIsOverviewVisible(false);
      } else if (newVisibleDayId) {
        setVisibleDayId(newVisibleDayId);
        setVisibleStopId(null);
        setIsOverviewVisible(false);
      }
    }, options);

    // Observe all current stop and day elements
    stopElementsRef.current.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    dayElementsRef.current.forEach((element) => {
      if (observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    // Observe overview element if it exists
    if (overviewElementRef.current && observerRef.current) {
      observerRef.current.observe(overviewElementRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [days]); // Only depend on days, not the state variables we're updating

  // Register stop elements for observation
  const registerStopElement = useCallback(
    (stopId: string, element: HTMLDivElement | null) => {
      if (element) {
        stopElementsRef.current.set(stopId, element);
        if (observerRef.current) {
          observerRef.current.observe(element);
        }
      } else {
        const existingElement = stopElementsRef.current.get(stopId);
        if (existingElement && observerRef.current) {
          observerRef.current.unobserve(existingElement);
        }
        stopElementsRef.current.delete(stopId);
      }
    },
    []
  );

  // Register day elements for observation
  const registerDayElement = useCallback(
    (dayId: string, element: HTMLDivElement | null) => {
      if (element) {
        dayElementsRef.current.set(dayId, element);
        if (observerRef.current) {
          observerRef.current.observe(element);
        }
      } else {
        const existingElement = dayElementsRef.current.get(dayId);
        if (existingElement && observerRef.current) {
          observerRef.current.unobserve(existingElement);
        }
        dayElementsRef.current.delete(dayId);
      }
    },
    []
  );

  const handleStopSelect = (
    stop: Stop,
    dayNumber: number,
    stopNumber: number
  ) => {
    setSelectedStop(stop);
  };

  const handleStopClick = (stop: Stop, dayIndex: number, stopIndex: number) => {
    // Set scrolling state to prevent map zoom effects and intersection observer updates
    setIsScrolling(true);
    isScrollingRef.current = true;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Find the stop element and scroll to it in the document
    const stopElement = stopElementsRef.current.get(stop.id);

    if (stopElement) {
      // Calculate the position of the stop element relative to the viewport
      const stopRect = stopElement.getBoundingClientRect();
      const mapHeight = window.innerHeight * 0.35; // 35vh map height
      
      // Calculate the scroll position needed - account for fixed map height plus offset
      const scrollTop = window.scrollY + stopRect.top - mapHeight - 20; // 20px offset

      // Scroll the document
      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });

      // Also set as selected stop for map highlighting
      setSelectedStop(stop);

      // Reset scrolling state after scroll animation completes
      scrollTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
        isScrollingRef.current = false;
      }, 800);
    } else {
      // If we can't find elements, reset scrolling state immediately
      setIsScrolling(false);
      isScrollingRef.current = false;
    }
  };

  const handleOverviewStopClick = (stopId: string) => {
    // Set scrolling state to prevent map zoom effects and intersection observer updates
    setIsScrolling(true);
    isScrollingRef.current = true;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Find the stop and scroll to it
    const stop = stopsData?.allStops.find((s) => s.id === stopId);
    if (stop) {
      const stopElement = stopElementsRef.current.get(stopId);

      if (stopElement) {
        const stopRect = stopElement.getBoundingClientRect();
        const mapHeight = window.innerHeight * 0.35; // 35vh map height
        const scrollTop = window.scrollY + stopRect.top - mapHeight - 20;

        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });

        setSelectedStop(stop);

        // Reset scrolling state after scroll animation completes
        scrollTimeoutRef.current = setTimeout(() => {
          setIsScrolling(false);
          isScrollingRef.current = false;
        }, 800);
      } else {
        setIsScrolling(false);
        isScrollingRef.current = false;
      }
    } else {
      setIsScrolling(false);
      isScrollingRef.current = false;
    }
  };

  const handleGotoDayClick = (dayId: string) => {
    // Set scrolling state to prevent map zoom effects and intersection observer updates
    setIsScrolling(true);
    isScrollingRef.current = true;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Find the day and scroll to its first stop
    const dayStops = stopsData?.getStopsForDay(dayId);
    if (dayStops && dayStops.length > 0) {
      const firstStop = dayStops[0];
      const stopElement = stopElementsRef.current.get(firstStop.id);

      if (stopElement) {
        const stopRect = stopElement.getBoundingClientRect();
        const mapHeight = window.innerHeight * 0.35; // 35vh map height
        const scrollTop = window.scrollY + stopRect.top - mapHeight - 20;

        window.scrollTo({
          top: scrollTop,
          behavior: 'smooth',
        });

        setSelectedStop(firstStop);

        // Reset scrolling state after scroll animation completes
        scrollTimeoutRef.current = setTimeout(() => {
          setIsScrolling(false);
          isScrollingRef.current = false;
        }, 800);
      } else {
        setIsScrolling(false);
        isScrollingRef.current = false;
      }
    } else {
      setIsScrolling(false);
      isScrollingRef.current = false;
    }
  };

  // Get the currently visible stop and day objects
  const visibleStop = visibleStopId
    ? stopsData?.allStops.find((stop) => stop.id === visibleStopId)
    : null;

  const visibleDay = visibleDayId
    ? days?.find((day) => day.id === visibleDayId)
    : null;

  if (isLoadingItineraries || isLoadingDays) {
    return (
      <div className="flex items-center justify-center min-h-[50dvh]">
        Loading...
      </div>
    );
  }

  if (errorItineraries || errorDays) {
    return (
      <div className="flex items-center justify-center min-h-[50dvh]">
        Error loading itinerary
      </div>
    );
  }

  if (!itinerary) {
    return (
      <div className="flex items-center justify-center min-h-[50dvh]">
        Itinerary not found
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Fixed Map Section - 35vh height */}
      <div className="sticky top-0 left-0 right-0 h-[35vh] z-10 shadow-lg">
        <ItineraryMapSection
          days={days}
          stops={stopsData?.allStops || []}
          selectedStop={selectedStop}
          visibleStop={visibleStop}
          visibleDay={visibleDay}
          isOverviewVisible={isOverviewVisible}
          isScrolling={isScrolling}
          onStopSelect={handleStopSelect}
          onStopClick={handleStopClick}
          currentSection={{
            id: 'overview',
            type: 'overview',
            title: 'Overview',
          }} // Simple default
        />
      </div>

      {/* Scrollable Content - starts below the fixed map */}
      <div>
        {/* Author Banner */}
        {itinerary.account && (
          <ItineraryAuthorBanner account={itinerary.account} />
        )}

        {/* Overview Section */}
        <div
          className="bg-white rounded-lg p-6"
          style={{ scrollMarginTop: 'calc(35vh + 20px)' }}
          data-overview="true"
          ref={overviewElementRef}
        >
          <ItineraryOverviewSection
            itinerary={itinerary}
            days={days}
            stops={stopsData?.allStops || []}
          />
        </div>

        {/* Days overview */}
        <div
          className="px-1.5 pb-4 max-w-full mx-0 sm:max-w-3xl sm:mx-auto"
          style={{ scrollMarginTop: 'calc(35vh + 20px)' }}
        >
          <ItineraryDaysOverviewSection
            days={days}
            onStopClick={handleOverviewStopClick}
            onGotoDayClick={handleGotoDayClick}
          />
        </div>

        {/* Days and Stops */}
        {(days || []).map((day, dayIndex) => (
          <div key={dayIndex} className="space-y-4">
            {/* Day Section */}
            <div
              className="px-1.5 pb-4 bg-white"
              style={{ scrollMarginTop: 'calc(35vh + 20px)' }}
              data-day-id={day.id}
              ref={(el) => registerDayElement(day.id, el)}
            >
              <ItineraryDaySection
                day={day}
                dayIndex={dayIndex}
                stops={stopsData?.getStopsForDay(day.id) || []}
                onStopClick={handleOverviewStopClick}
              />
            </div>

            {/* Individual Stop Sections */}
            {stopsData?.getStopsForDay(day.id).map((stop, stopIndex) => (
              <div
                key={`${dayIndex}-${stopIndex}`}
                className="bg-white rounded-lg px-1.5 pb-4"
                style={{ scrollMarginTop: 'calc(35vh + 20px)' }}
                data-stop-id={stop.id}
                ref={(el) => registerStopElement(stop.id, el)}
              >
                <ItineraryStopSection
                  stop={stop}
                  stopIndex={stopIndex}
                  dayIndex={dayIndex}
                />
              </div>
            ))}
          </div>
        ))}

        {/* Author Section */}
        {itinerary.account && (
          <div>
            <ItineraryAuthorSection account={itinerary.account} />
          </div>
        )}
      </div>
    </div>
  );
}
